import { DateTime } from 'luxon';
import { JobStatus } from '../../../__generated__/graphql';

/**
 * Service job reviews (spec 019 R16, W19, W21). The customer reviews the
 * worker; the worker (or the Provider) reviews the customer. Both are open for
 * 14 days after the job is finished, and each side sees only its own review
 * until both are in or the 14 days pass. Mirrors the backend `service-reviews.ts`.
 */

export const SERVICE_REVIEW_WINDOW_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

export type ServiceReviewer = 'customer' | 'worker';

export type CustomerCategory =
  | 'thoroughness'
  | 'punctuality'
  | 'communication'
  | 'photoCompleteness';
export type WorkerCategory = 'accuracy' | 'access' | 'communication';

export const CUSTOMER_REVIEW_CATEGORIES: { key: CustomerCategory; label: string; hint: string }[] =
  [
    { key: 'thoroughness', label: 'Thoroughness', hint: 'Was everything on the list done well?' },
    { key: 'punctuality', label: 'On time', hint: 'Did the worker arrive in the window?' },
    { key: 'communication', label: 'Communication', hint: 'Were they easy to reach?' },
    { key: 'photoCompleteness', label: 'Photos', hint: 'Did the photos show the work?' },
  ];

export const WORKER_REVIEW_CATEGORIES: { key: WorkerCategory; label: string; hint: string }[] = [
  { key: 'accuracy', label: 'Accuracy', hint: 'Was the job as booked (size, mess, extras)?' },
  { key: 'access', label: 'Access', hint: 'Was getting in easy?' },
  { key: 'communication', label: 'Communication', hint: 'Was the customer easy to reach?' },
];

const FINISHED = new Set<JobStatus>([
  JobStatus.Completed,
  JobStatus.CustomerConfirmed,
  JobStatus.Disputed,
  JobStatus.Resolved,
]);

/** When the review window closes, or null while the job is not finished. */
export function serviceReviewClosesAt(
  jobStatus: JobStatus | null | undefined,
  completedAt: string | null | undefined,
): number | null {
  if (!jobStatus || !FINISHED.has(jobStatus) || !completedAt) return null;
  const done = Date.parse(completedAt);
  return Number.isFinite(done) ? done + SERVICE_REVIEW_WINDOW_DAYS * DAY_MS : null;
}

/** Scores are whole numbers from 1 to 5. */
export function scoresComplete(
  scores: Record<string, number | undefined>,
  keys: string[],
): boolean {
  return keys.every(key => {
    const value = scores[key];
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
  });
}

export type ServiceReviewView =
  | { kind: 'hidden' }
  | { kind: 'write'; text: string }
  | { kind: 'waiting'; text: string }
  | { kind: 'done'; text: string }
  | { kind: 'closed'; text: string };

/** What the review card says to one side. `mine` / `theirs` are when each side reviewed. */
export function serviceReviewView(input: {
  jobStatus: JobStatus | null | undefined;
  completedAt: string | null | undefined;
  mine?: string | null;
  theirs?: string | null;
  otherName: string;
  zone?: string | null;
  now?: number;
}): ServiceReviewView {
  const closesAt = serviceReviewClosesAt(input.jobStatus, input.completedAt);
  if (closesAt === null) return { kind: 'hidden' };
  const now = input.now ?? Date.now();
  const by = DateTime.fromMillis(closesAt, { zone: input.zone || 'UTC' }).toFormat('MMM d');
  const open = now < closesAt;
  if (input.mine) {
    if (input.theirs) return { kind: 'done', text: 'You both left reviews.' };
    return open
      ? {
          kind: 'waiting',
          text: `Thanks for your review. ${input.otherName} sees it once they write theirs, or on ${by}.`,
        }
      : { kind: 'done', text: `Your review is public. ${input.otherName} did not write one.` };
  }
  if (open) {
    return {
      kind: 'write',
      text: `Reviews show once you both write one, or on ${by}. Write yours by ${by}.`,
    };
  }
  return { kind: 'closed', text: 'The review window for this job has closed.' };
}
